import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stethoscope, MessageCircle, Phone, Activity, ArrowRight } from 'lucide-react';
import { MagicCard } from './ui/magic/MagicCard';

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode;
  className: string;
  action?: () => void;
  actionText?: string;
}

export const WhyChooseSection: React.FC = () => {
  const navigate = useNavigate();
  const features: Feature[] = [
    {
      title: "Symptom Analysis",
      description: "The people you serve describe their symptoms and get detailed analysis of possible conditions, severity levels, and recommended treatment options.",
      icon: <Stethoscope className="w-6 h-6 text-[#003b82]" />,
      className: "md:col-span-2",
      action: () => window.open("mailto:founders@mycura.org?subject=Demo%20Request", "_self"),
      actionText: "Request a Demo"
    },
    {
      title: "WhatsApp Diagnosis",
      description: "Immediate diagnostic insights through simple messaging on WhatsApp, a channel your community already knows.",
      icon: <MessageCircle className="w-6 h-6 text-[#003b82]" />,
      className: "md:col-span-1"
    },
    {
      title: "Voice Diagnosis",
      description: "The people you serve simply call and describe their symptoms to get instant medical insights through natural conversation.",
      icon: <Phone className="w-6 h-6 text-[#003b82]" />,
      className: "md:col-span-1"
    },
    {
      title: "Smart Triage",
      description: "Know exactly when professional help is needed. Our AI guides the people you serve to the appropriate level of care.",
      icon: <Activity className="w-6 h-6 text-[#003b82]" />,
      className: "md:col-span-2",
      action: () => navigate('/caregivers'),
      actionText: "Learn More"
    }
  ];

  return (
    <section id="features" className="py-24 bg-white border-t border-slate-200 relative">
      {/* Full height vertical lines at container edges */}
      <div className="absolute inset-0 max-w-7xl mx-auto pointer-events-none">
        <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-200"></div>
        <div className="absolute right-0 top-0 bottom-0 w-px bg-slate-200"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl text-slate-900 font-medium mb-6">
            Complete Medical Intelligence
          </h2>
          <p className="text-lg text-slate-600 font-medium">
            Personalized healthcare technology that adapts to the people your organization serves, available 24/7 through the channels they already use.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={feature.className}
            >
              <MagicCard className="h-full overflow-hidden rounded-2xl p-8 transition-all duration-300 hover:shadow-xl border border-slate-100 bg-[#F6F8FC] hover:bg-white">
                <div className="relative z-10 flex flex-col h-full">
                  <div className="mb-6 inline-flex self-start p-3 rounded-xl bg-white shadow-sm border border-slate-100">
                    {feature.icon}
                  </div>

                  <h3 className="text-xl text-[#003b82] font-semibold mb-3">
                    {feature.title}
                  </h3>

                  <p className="text-slate-700 flex-grow leading-relaxed">
                    {feature.description}
                  </p>

                  {feature.action && feature.actionText && (
                    <button
                      onClick={feature.action}
                      className="mt-8 inline-flex items-center self-start text-sm font-semibold text-[#003b82] group-hover:translate-x-1 transition-transform"
                    >
                      {feature.actionText}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  )}
                </div>
              </MagicCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
